import axios from 'axios';
import { API_BASE } from '../services/apiConfig';

let refreshPromise = null;

/** Absolute time when the current access-token cookie is expected to expire. */
let accessExpiresAtMs = 0;
let proactiveTimer = null;
let listenersArmed = false;

/** Fallback when the API does not return expiresIn (keep in sync with Jwt:AccessTokenMinutes). */
export const DEFAULT_ACCESS_TOKEN_MS = 30 * 60 * 1000;
/** Refresh this far before expiry so callers rarely hit the 401 → retry path. */
const PROACTIVE_SKEW_MS = 90_000;
const MIN_PROACTIVE_DELAY_MS = 5_000;

const refreshClient = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  withCredentials: true,
});

export function isAuthEndpointUrl(url = '') {
  const u = (url || '').toLowerCase();
  return (
    u.includes('/webusers/login') ||
    u.includes('webusers/login') ||
    u.includes('/webusers/refresh') ||
    u.includes('webusers/refresh') ||
    u.includes('/webusers/logout') ||
    u.includes('webusers/logout') ||
    u.includes('/webusers/windows-login') ||
    u.includes('webusers/windows-login') ||
    u.includes('/webusers/windows-password-login') ||
    u.includes('webusers/windows-password-login')
  );
}

export function redirectToLoginExpired() {
  clearSessionSchedule();
  try {
    localStorage.removeItem('username');
    localStorage.removeItem('roleId');
    localStorage.removeItem('permissions');
  } catch { /* ignore */ }

  if (typeof window !== 'undefined') {
    const path = window.location.pathname || '';
    if (path === '/login' || path.startsWith('/login/')) return;
    window.location.assign('/login?reason=expired');
  }
}

/**
 * Record when the access token will expire and arm a silent proactive refresh.
 * @param {number} [expiresInSeconds] remaining lifetime from the API
 */
export function noteAccessTokenLifetime(expiresInSeconds) {
  const seconds = Number(expiresInSeconds);
  const ms = Number.isFinite(seconds) && seconds > 0
    ? seconds * 1000
    : DEFAULT_ACCESS_TOKEN_MS;
  accessExpiresAtMs = Date.now() + ms;
  armProactiveRefresh();
  ensureProactiveRefreshListeners();
}

export function clearSessionSchedule() {
  if (proactiveTimer != null) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
  accessExpiresAtMs = 0;
}

function armProactiveRefresh() {
  if (proactiveTimer != null) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
  if (!accessExpiresAtMs) return;

  const delay = Math.max(MIN_PROACTIVE_DELAY_MS, accessExpiresAtMs - Date.now() - PROACTIVE_SKEW_MS);
  proactiveTimer = setTimeout(() => {
    proactiveTimer = null;
    refreshSession().catch(() => {
      // Failure surfaces on the next authenticated call.
    });
  }, delay);
}

function maybeRefreshIfNearExpiry() {
  if (!accessExpiresAtMs) return;
  const remaining = accessExpiresAtMs - Date.now();
  if (remaining <= PROACTIVE_SKEW_MS) {
    refreshSession().catch(() => {});
  }
}

/** Focus / tab-visible: refresh early if the access token is about to expire. */
export function ensureProactiveRefreshListeners() {
  if (listenersArmed || typeof window === 'undefined') return;
  listenersArmed = true;

  const onVisible = () => {
    if (document.visibilityState === 'visible') maybeRefreshIfNearExpiry();
  };
  window.addEventListener('focus', onVisible);
  document.addEventListener('visibilitychange', onVisible);
}

function readExpiresIn(data) {
  if (data == null || typeof data !== 'object') return null;
  const raw = data.expiresIn ?? data.ExpiresIn ?? data.expiresInSeconds;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Refresh access-token cookie using the HttpOnly refresh_token cookie. De-dupes concurrent calls. */
export async function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const { data } = await refreshClient.post('webusers/refresh', null);
        noteAccessTokenLifetime(readExpiresIn(data) ?? DEFAULT_ACCESS_TOKEN_MS / 1000);
        return data;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

export function getRefreshPromise() {
  return refreshPromise;
}
